type SetFn = (
  handler: TimerHandler,
  timeout?: number,
  ...args: unknown[]
) => number;
type ClearFn = (id: number | undefined) => void;

interface SetOptions {
  reset?: boolean;
}

class TaskManager {
  type = 'task';

  _set: SetFn = undefined as unknown as SetFn;

  _clear: ClearFn = undefined as unknown as ClearFn;

  _instances: Record<string, number> = {};

  /**
   * Creates a task and stores it by name.
   * @param {string} name - The name to associate with the task .
   * @param {Function} callback - The function to be called after the delay.
   * @param {number} delay - The delay in milliseconds before the callback is executed.
   * @param {Object} options - Additional options.
   * @param {boolean} options.reset - Whether to reset the task if it already exists. Default is true.
   */
  set(
    name: string,
    callback: () => void,
    delay: number,
    options: SetOptions = {},
  ) {
    const { reset = true } = options;

    if (reset) {
      this.clear(name);
    }

    try {
      this._instances[name] = this._set(() => {
        callback();
        delete this._instances[name];
      }, delay);
      if (import.meta.env.MODE === 'development') {
        console.debug(`The ${this.type} with name - '${name}' has been set`);
      }
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        console.error(
          `Unable to set the ${this.type} with name - '${name}'`,
          error,
        );
      }
    }
  }

  /**
   * Resets a task by its name.
   * @param {string} name - The name of the task to reset.
   */
  clear(name: string) {
    if (this._instances[name]) {
      try {
        this._clear(this._instances[name]);
        delete this._instances[name];

        if (import.meta.env.MODE === 'development') {
          console.debug(
            `The ${this.type} with name - '${name}' has been reset`,
          );
        }
      } catch (error) {
        if (import.meta.env.MODE === 'development') {
          console.warn(
            `Unable to reset the ${this.type} with name - '${name}'`,
            error,
          );
        }
      }
    }
  }

  invalidate() {
    for (const name in this._instances) {
      this.clear(name);
    }
  }
}

class TimeoutManager extends TaskManager {
  type = 'timeout';

  _set: SetFn = window.setTimeout.bind(window);

  _clear: ClearFn = window.clearTimeout.bind(window);
}

class IntervalManager extends TaskManager {
  type = 'interval';

  _set: SetFn = window.setInterval.bind(window);

  _clear: ClearFn = window.clearInterval.bind(window);
}

export { IntervalManager, TimeoutManager };
